import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicHeader from '@/components/layout/PublicHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Shield, Brain, Target, TrendingUp, Users, FileText, Zap, CheckCircle,
  ArrowRight, Network, BookOpen, Sparkles, BarChart3, Clock, AlertTriangle,
  XCircle, Minus, ChevronRight, Globe, Lock, Layers, MessageCircle,
  DollarSign, Calculator
} from 'lucide-react';

const Check = () => <CheckCircle className="w-4 h-4 text-[#34C759]" />;
const Partial = () => <Minus className="w-4 h-4 text-[#FF9500]" />;
const No = () => <XCircle className="w-4 h-4 text-[#E5E7EB]" />;

const DIFFERENTIATORS = [
  {
    icon: Shield, color: '#002FA7',
    title: 'ISO 30401:2018 Compliance — Built-In, Not Bolted On',
    subtitle: 'The only KM platform with a native compliance center',
    description: 'KStreak ships with a complete ISO 30401:2018 Compliance Center that auto-evaluates all 25 clauses from real platform usage data. No spreadsheets, no external auditors — a live compliance score with evidence trails, updated in one click.',
    proof: 'Auto-evaluates 25 ISO clauses across 15+ platform metrics',
  },
  {
    icon: Users, color: '#34C759',
    title: 'People → Process → Technology Framework',
    subtitle: 'Not just a document store — a KM discipline platform',
    description: 'Unlike Confluence or SharePoint (glorified wikis), KStreak is structured around the knowledge management discipline itself. Capability Assessments with AI-powered maturity recommendations, certification verification with HR review, competency tracking — all people-first.',
    proof: 'GPT-5.2 generates 7-area KM improvement plans by maturity level',
  },
  {
    icon: Target, color: '#FF3B30',
    title: 'Closed-Loop Knowledge Lifecycle',
    subtitle: 'From creation to degradation to prediction to correction',
    description: 'Most platforms stop at "store and search." KStreak tracks knowledge through its entire lifecycle: articles degrade? Flagged. Gaps emerge? AI predicts them. Help desk tickets reveal missing knowledge? Auto-drafts KB articles. Lessons learned? One-click process improvement tasks.',
    proof: 'Degradation → Gap Prediction → Help Desk → KB Draft → Task',
  },
  {
    icon: Brain, color: '#AF52DE',
    title: 'Predictive Intelligence, Not Just Reporting',
    subtitle: 'Know what will happen, not just what happened',
    description: 'Scenario comparisons, key driver analysis, prediction accuracy tracking, and prescriptive actions that auto-generate tasks. KStreak\'s AI doesn\'t just summarize — it predicts knowledge gaps before they become crises and recommends specific interventions.',
    proof: 'Prediction accuracy tracked over time with trend dashboards',
  },
  {
    icon: MessageCircle, color: '#FF9500',
    title: 'SLA-Driven Service Desk Integrated with KM',
    subtitle: 'A feedback loop, not a separate tool',
    description: 'The Help Desk isn\'t bolted on — it\'s a KM feedback engine. Ticket patterns drive knowledge gap analysis, self-service rates measure KB effectiveness, SLA breaches auto-escalate priorities, and the Impact Report ties tickets, articles, and gaps together.',
    proof: 'Auto-escalation, self-service tracking, knowledge gap analysis',
  },
  {
    icon: Sparkles, color: '#007AFF',
    title: 'Assessment-to-Action Pipeline',
    subtitle: 'Score → Recommend → Track → Verify → Improve',
    description: 'The Capability Assessment doesn\'t just score you — it generates AI recommendations grounded in People→Process→Technology, tracks completion with reminders, verifies certifications with HR, and feeds results back into the improvement loop.',
    proof: 'Email distribution, completion tracking, cert verification, AI recs',
  },
];

const COMPARISON = [
  { feature: 'ISO 30401:2018 Compliance Center', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Auto Compliance Scoring from Usage Data', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'AI-Powered KM Maturity Recommendations', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Predictive Intelligence & Scenario Analysis', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Knowledge Degradation Tracking', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Knowledge Gap Prediction (AI)', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'partial' },
  { feature: 'Capability Assessment with Certifications', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Lesson-to-Process Task Automation', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'KCS Methodology Workspace', kstreak: 'full', sharepoint: 'no', confluence: 'partial', notion: 'no', guru: 'partial' },
  { feature: 'Integrated Help Desk + KM Feedback Loop', kstreak: 'full', sharepoint: 'no', confluence: 'partial', notion: 'no', guru: 'partial' },
  { feature: 'SLA Tracking with Auto-Escalation', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Knowledge Dependency Mapping', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'AI Search with Intent & Gap Analysis', kstreak: 'full', sharepoint: 'partial', confluence: 'partial', notion: 'partial', guru: 'full' },
  { feature: 'Kanban Board with Task Workflow Engine', kstreak: 'full', sharepoint: 'partial', confluence: 'partial', notion: 'full', guru: 'no' },
  { feature: 'Document/Article Management', kstreak: 'full', sharepoint: 'full', confluence: 'full', notion: 'full', guru: 'full' },
  { feature: 'Webinar / Video Conferencing', kstreak: 'full', sharepoint: 'partial', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Voice Journaling with AI Transcription', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Recording Intelligence & Knowledge Clips', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'PDF Export & Email Sharing (All Tools)', kstreak: 'full', sharepoint: 'partial', confluence: 'partial', notion: 'partial', guru: 'partial' },
  { feature: 'Personal Knowledge Management (PKM)', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'partial', guru: 'no' },
  { feature: 'HR Workflows (Onboarding/Offboarding)', kstreak: 'full', sharepoint: 'partial', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Link Analysis & Anomaly Detection', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Management Review Log (Clause 9.3)', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
  { feature: 'Risk & Opportunity Register', kstreak: 'full', sharepoint: 'no', confluence: 'no', notion: 'no', guru: 'no' },
];

const CellIcon = ({ val }) => val === 'full' ? <Check /> : val === 'partial' ? <Partial /> : <No />;

const ROICalculator = () => {
  const [employees, setEmployees] = useState(50);
  const [avgSalary, setAvgSalary] = useState(75000);
  const [hoursSearching, setHoursSearching] = useState(8);
  const [onboardingWeeks, setOnboardingWeeks] = useState(6);
  const [newHiresYear, setNewHiresYear] = useState(10);
  const [ticketsMonth, setTicketsMonth] = useState(120);
  const [repeatedMistakes, setRepeatedMistakes] = useState(5);
  const [costPerMistake, setCostPerMistake] = useState(2000);

  const hourlyRate = avgSalary / 2080;

  // 1. Time savings from faster knowledge search (reduce by 60%)
  const searchTimeSaved = employees * hoursSearching * 0.6 * 52;
  const searchSavings = searchTimeSaved * hourlyRate;

  // 2. Onboarding acceleration (reduce by 40%)
  const onboardingWeeksSaved = onboardingWeeks * 0.4;
  const onboardingSavings = newHiresYear * onboardingWeeksSaved * 40 * hourlyRate;

  // 3. Help desk self-service (35% self-service deflection)
  const ticketCost = hourlyRate * 0.5;
  const deflectedTickets = ticketsMonth * 12 * 0.35;
  const ticketSavings = deflectedTickets * ticketCost;

  // 4. Reduced repeated mistakes (reduce by 50%)
  const mistakeSavings = repeatedMistakes * 12 * 0.5 * costPerMistake;

  // 5. Knowledge retention (avoid 15% productivity loss per departure)
  const attritionRate = 0.12;
  const departures = Math.round(employees * attritionRate);
  const retentionSavings = departures * avgSalary * 0.05;

  const totalSavings = searchSavings + onboardingSavings + ticketSavings + mistakeSavings + retentionSavings;
  const kstreakCost = employees * 15 * 12;
  const netROI = totalSavings - kstreakCost;
  const roiPercent = Math.round((netROI / Math.max(kstreakCost, 1)) * 100);
  const paybackMonths = totalSavings > 0 ? Math.max(1, Math.round((kstreakCost / totalSavings) * 12)) : 0;

  const fmt = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${Math.round(n)}`;

  const savingsBreakdown = [
    { label: 'Knowledge Search Time', value: searchSavings, color: '#002FA7', desc: `${Math.round(searchTimeSaved)} hours saved/yr (60% faster)` },
    { label: 'Faster Onboarding', value: onboardingSavings, color: '#34C759', desc: `${onboardingWeeksSaved.toFixed(1)} weeks saved × ${newHiresYear} hires` },
    { label: 'Ticket Self-Service', value: ticketSavings, color: '#FF9500', desc: `${Math.round(deflectedTickets)} tickets deflected/yr (35%)` },
    { label: 'Fewer Repeated Mistakes', value: mistakeSavings, color: '#FF3B30', desc: `${Math.round(repeatedMistakes * 12 * 0.5)} incidents prevented/yr (50%)` },
    { label: 'Knowledge Retention', value: retentionSavings, color: '#AF52DE', desc: `${departures} departures/yr — knowledge captured` },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
      {/* Inputs */}
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-6 space-y-5">
          <h3 className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-2"><Calculator className="w-4 h-4 text-[#002FA7]" />Your Organization</h3>

          <SliderInput label="Number of Knowledge Workers" value={employees} onChange={setEmployees} min={5} max={5000} step={5} suffix=" people" testId="roi-employees" />
          <SliderInput label="Average Annual Salary" value={avgSalary} onChange={setAvgSalary} min={30000} max={200000} step={5000} prefix="$" testId="roi-salary" />
          <SliderInput label="Hours/Week Spent Searching for Knowledge" value={hoursSearching} onChange={setHoursSearching} min={1} max={20} step={1} suffix=" hrs/week" testId="roi-search-hours" />
          <SliderInput label="Onboarding Time for New Hires" value={onboardingWeeks} onChange={setOnboardingWeeks} min={1} max={24} step={1} suffix=" weeks" testId="roi-onboarding" />
          <SliderInput label="New Hires per Year" value={newHiresYear} onChange={setNewHiresYear} min={0} max={500} step={1} suffix=" hires/yr" testId="roi-new-hires" />
          <SliderInput label="Support Tickets per Month" value={ticketsMonth} onChange={setTicketsMonth} min={0} max={2000} step={10} suffix=" /month" testId="roi-tickets" />
          <SliderInput label="Repeated Mistakes per Month" value={repeatedMistakes} onChange={setRepeatedMistakes} min={0} max={50} step={1} suffix=" /month" testId="roi-mistakes" />
          <SliderInput label="Average Cost per Mistake" value={costPerMistake} onChange={setCostPerMistake} min={100} max={50000} step={100} prefix="$" testId="roi-mistake-cost" />
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-4">
        {/* ROI Summary */}
        <Card className="border-[#002FA7]/20 bg-[#002FA7]/[0.02]" data-testid="roi-summary">
          <CardContent className="p-5 text-center">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Estimated Annual ROI</p>
            <p className="text-4xl font-bold text-[#002FA7]" data-testid="roi-percent">{roiPercent > 0 ? `${roiPercent}%` : '—'}</p>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="p-2 rounded-lg bg-white border border-[#E5E7EB]">
                <p className="text-lg font-bold text-[#34C759]" data-testid="roi-total-savings">{fmt(totalSavings)}</p>
                <p className="text-[8px] text-[#9CA3AF]">Annual Savings</p>
              </div>
              <div className="p-2 rounded-lg bg-white border border-[#E5E7EB]">
                <p className="text-lg font-bold text-[#FF3B30]">{fmt(kstreakCost)}</p>
                <p className="text-[8px] text-[#9CA3AF]">KStreak Cost</p>
              </div>
              <div className="p-2 rounded-lg bg-white border border-[#E5E7EB]">
                <p className="text-lg font-bold text-[#002FA7]" data-testid="roi-net">{fmt(netROI)}</p>
                <p className="text-[8px] text-[#9CA3AF]">Net Benefit</p>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#34C759]/10 text-[#34C759] text-xs font-medium" data-testid="roi-payback">
              <Clock className="w-3 h-3" />Payback in ~{paybackMonths} month{paybackMonths !== 1 ? 's' : ''}
            </div>
          </CardContent>
        </Card>

        {/* Breakdown */}
        <Card className="border-[#E5E7EB]" data-testid="roi-breakdown">
          <CardContent className="p-5 space-y-3">
            <h3 className="text-xs font-semibold text-[#0A0A0A]">Savings Breakdown</h3>
            {savingsBreakdown.map((s, i) => {
              const pct = totalSavings > 0 ? Math.round((s.value / totalSavings) * 100) : 0;
              return (
                <div key={i} data-testid={`roi-item-${i}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-medium text-[#0A0A0A]">{s.label}</span>
                    <span className="text-xs font-bold" style={{ color: s.color }}>{fmt(s.value)}</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: s.color }} />
                  </div>
                  <p className="text-[9px] text-[#9CA3AF] mt-0.5">{s.desc}</p>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <p className="text-[9px] text-[#9CA3AF] text-center">
          Based on industry research: McKinsey (19.7% productivity from searchability), APQC (knowledge sharing benchmarks), HDI (ticket deflection rates). Actual results vary by organization.
        </p>
      </div>
    </div>
  );
};

const SliderInput = ({ label, value, onChange, min, max, step, prefix = '', suffix = '', testId }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-xs text-[#4B5563]">{label}</label>
      <span className="text-xs font-bold text-[#0A0A0A]">{prefix}{value.toLocaleString()}{suffix}</span>
    </div>
    <Slider value={[value]} onValueChange={v => onChange(v[0])} min={min} max={max} step={step} className="w-full" />
  </div>
);

const WhyKStreakPage = () => {
  const kstreakFull = COMPARISON.filter(c => c.kstreak === 'full').length;
  const spFull = COMPARISON.filter(c => c.sharepoint === 'full').length;
  const confFull = COMPARISON.filter(c => c.confluence === 'full').length;
  const notFull = COMPARISON.filter(c => c.notion === 'full').length;
  const guruFull = COMPARISON.filter(c => c.guru === 'full').length;

  return (
    <div className="min-h-screen bg-white" data-testid="why-kstreak-page">
      <PublicHeader />
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-[#007AFF] text-sm hover:underline">Home</Link>
            <ChevronRight className="w-3 h-3 text-[#4B5563]" />
            <span className="text-sm text-[#9CA3AF]">Why KStreak</span>
          </div>
          <Badge className="bg-[#002FA7] text-white mb-4 text-xs">ISO 30401:2018 Compliant</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            What Makes <span className="text-[#002FA7]">KStreak</span> Different
          </h1>
          <p className="text-lg sm:text-xl text-[#9CA3AF] max-w-2xl mb-8">
            Not another wiki. Not another document store. KStreak is the first knowledge management platform built around the <strong className="text-white">People → Process → Technology</strong> framework with native ISO 30401 compliance.
          </p>
          <div className="flex gap-3">
            <Link to="/register"><Button className="bg-[#002FA7] hover:bg-[#001F7A] text-base px-6 py-3 h-auto">Start Free Trial<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/demo"><Button variant="outline" className="border-[#333] text-white hover:bg-[#1A1A1A] text-base px-6 py-3 h-auto">Watch Demo</Button></Link>
          </div>
        </div>
      </div>

      {/* Differentiators */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">6 Capabilities No Other KM Platform Has</h2>
          <p className="text-[#4B5563] max-w-xl mx-auto">Each one addresses a real gap in how organizations manage knowledge today.</p>
        </div>

        <div className="space-y-6">
          {DIFFERENTIATORS.map((d, i) => {
            const Icon = d.icon;
            return (
              <Card key={i} className="border-[#E5E7EB] overflow-hidden hover:shadow-md transition-shadow" data-testid={`diff-${i}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-16 flex-shrink-0 flex items-center justify-center py-4 lg:py-0" style={{ backgroundColor: d.color + '08' }}>
                      <Icon className="w-7 h-7" style={{ color: d.color }} />
                    </div>
                    <div className="flex-1 p-5 lg:p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-[#0A0A0A] mb-0.5">{d.title}</h3>
                          <p className="text-xs text-[#007AFF] font-medium mb-2">{d.subtitle}</p>
                          <p className="text-sm text-[#4B5563] leading-relaxed">{d.description}</p>
                        </div>
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium" style={{ backgroundColor: d.color + '10', color: d.color }}>
                        <Zap className="w-3 h-3" />{d.proof}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Competitive Comparison Matrix */}
      <div className="bg-[#FAFAFA] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">Feature Comparison Matrix</h2>
            <p className="text-[#4B5563] max-w-xl mx-auto">How KStreak compares to mainstream knowledge tools across {COMPARISON.length} capabilities.</p>
          </div>

          {/* Score Summary */}
          <div className="grid grid-cols-5 gap-3 mb-8 max-w-3xl mx-auto">
            {[
              { name: 'KStreak', score: kstreakFull, color: '#002FA7', bold: true },
              { name: 'SharePoint', score: spFull, color: '#4B5563' },
              { name: 'Confluence', score: confFull, color: '#4B5563' },
              { name: 'Notion', score: notFull, color: '#4B5563' },
              { name: 'Guru', score: guruFull, color: '#4B5563' },
            ].map((p, i) => (
              <div key={i} className={`text-center p-3 rounded-lg ${p.bold ? 'bg-[#002FA7] text-white' : 'bg-white border border-[#E5E7EB]'}`} data-testid={`score-${p.name.toLowerCase()}`}>
                <p className="text-2xl font-bold">{p.score}<span className="text-sm font-normal opacity-70">/{COMPARISON.length}</span></p>
                <p className={`text-xs ${p.bold ? 'text-white/80' : 'text-[#9CA3AF]'}`}>{p.name}</p>
              </div>
            ))}
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white" data-testid="comparison-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0A0A0A] text-white">
                  <th className="text-left px-4 py-3 text-xs font-semibold w-[40%]">Capability</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold bg-[#002FA7]">KStreak</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold">SharePoint</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold">Confluence</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold">Notion</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold">Guru</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={i} className={`border-t border-[#F3F4F6] ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}`} data-testid={`row-${i}`}>
                    <td className="px-4 py-2.5 text-xs text-[#0A0A0A] font-medium">{row.feature}</td>
                    <td className="text-center px-3 py-2.5 bg-[#002FA7]/[0.02]"><CellIcon val={row.kstreak} /></td>
                    <td className="text-center px-3 py-2.5"><CellIcon val={row.sharepoint} /></td>
                    <td className="text-center px-3 py-2.5"><CellIcon val={row.confluence} /></td>
                    <td className="text-center px-3 py-2.5"><CellIcon val={row.notion} /></td>
                    <td className="text-center px-3 py-2.5"><CellIcon val={row.guru} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center gap-6 mt-4 text-xs text-[#9CA3AF]">
            <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-[#34C759]" /> Full support</span>
            <span className="flex items-center gap-1"><Minus className="w-3.5 h-3.5 text-[#FF9500]" /> Partial / Add-on</span>
            <span className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-[#E5E7EB]" /> Not available</span>
          </div>
        </div>
      </div>

      {/* ROI Calculator */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16" data-testid="roi-calculator">
        <div className="text-center mb-10">
          <Badge className="bg-[#34C759] text-white mb-3 text-xs">Interactive Calculator</Badge>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">Calculate Your KM Return on Investment</h2>
          <p className="text-[#4B5563] max-w-xl mx-auto">Adjust the sliders to match your organization. See how KStreak pays for itself through time savings, faster onboarding, fewer repeated mistakes, and reduced ticket volume.</p>
        </div>

        <ROICalculator />
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">Ready to transform your knowledge management?</h2>
        <p className="text-[#4B5563] max-w-lg mx-auto mb-6">Join organizations using KStreak to build a people-first, ISO-compliant knowledge culture.</p>
        <div className="flex justify-center gap-3">
          <Link to="/register"><Button className="bg-[#002FA7] hover:bg-[#001F7A] text-base px-8 py-3 h-auto">Get Started Free<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          <Link to="/"><Button variant="outline" className="text-base px-8 py-3 h-auto">Back to Home</Button></Link>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#E5E7EB] py-6 text-center text-xs text-[#9CA3AF]">
        KStreak Knowledge Management System | ISO 30401:2018 Compliant
      </div>
    </div>
  );
};

export default WhyKStreakPage;
